import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Button } from '@/components/ui';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary - Catch-all component for runtime application errors.
 * Provides a clean UI for partial application failures.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in application boundary:', error, errorInfo);
  }

  public handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white px-6">
          <div className="w-20 h-20 rounded-2xl bg-primary/8 border border-primary/20 flex items-center justify-center mb-8 shadow-glow">
            <i className="ri-error-warning-line text-4xl text-primary animate-pulse"></i>
          </div>
          
          <h2 className="text-3xl font-heading font-black text-secondary mb-4 leading-tight">
             Something went{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">wrong</span>
          </h2>
          
          <p className="text-gray-500 text-sm max-w-md text-center mb-10 leading-relaxed font-medium">
            We've encountered an unexpected error. This has been logged and we're looking into it. Please try refreshing the page.
          </p>

          <Button 
            onClick={this.handleReset}
            icon="ri-refresh-line"
            className="px-10 h-13"
          >
            Try Again
          </Button>
          
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-12 p-6 bg-dark/5 rounded-2xl border border-dark/10 max-w-2xl overflow-auto w-full">
               <pre className="text-[11px] font-mono text-secondary/60 whitespace-pre-wrap">
                 {this.state.error?.message}
               </pre>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
